import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { handleErrors } from '../../../actions/ErrorActions';
import { forceSavedSearchRefresh } from '../../../actions/SavedSearchesActions';
import { addSuccess } from '../../../actions/SuccessActions';
import { performRoleActorsAction } from '../../../api/role-actors-management';
import SavedSearchesTable from '../../../components/SavedSearchesTable';
import MultiSelectCell from '../../../components/TableSelect/MultiSelectCell';
import { selectAllHeader } from '../../../components/TableSelect/MultiSelectHeader';
import NavigateLinkCell from '../../../containers/FindPage/NavigateLinkCell';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { removeCurrentPageSelection } from '../../../utils/commonUtils';
import { ROLE_MANAGEMENT_ACTIONS_TYPES } from '../../constants/runtime.constants';

const useStyles = makeStyles({
  savedSearchContainer: {
    flex: 1,
  },

  actionButtonContainer: {
    display: 'flex',
    justifyContent: 'end',
    marginBottom: 8,
  },
});

const ActorsTable = ({
  entityName,
  searchName,
  roleId,
  actionType,
  buttonLabel,
  setIsLoading,
  customFilter = [],
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const [selectedActors, setSelectedActors] = useState([]);

  const isDeleteAction = actionType === ROLE_MANAGEMENT_ACTIONS_TYPES.DELETE;

  const handleMultiSelect = (checked, row) => {
    const newSelections = checked
      ? [...selectedActors, row]
      : selectedActors.filter((s) => s[s.PrimaryKeyColumn] !== row[row.PrimaryKeyColumn]);

    setSelectedActors(newSelections);
  };

  const handleHeaderMultiselect = (
    allRowsPerPage,
    isAllRecordsPerPageSelected,
    isSelectedRecordOnCurrentPage
  ) => {
    if (
      (isSelectedRecordOnCurrentPage && !isAllRecordsPerPageSelected) ||
      isAllRecordsPerPageSelected
    ) {
      const filteredSelections = removeCurrentPageSelection(allRowsPerPage, selectedActors);
      setSelectedActors(filteredSelections);
      return;
    }

    setSelectedActors([...selectedActors, ...allRowsPerPage]);
  };

  const extraCurrentColumns = [
    {
      index: 0,
      header: (rows) => selectAllHeader(rows, handleHeaderMultiselect, selectedActors),
      accessor: 'SELECT_ALL_HEADER',
      dataType: 'System.String',
      // eslint-disable-next-line react/display-name
      customCell: MultiSelectCell(selectedActors, handleMultiSelect),
    },
  ];

  const handleActionButtonClick = async () => {
    try {
      setIsLoading(true);

      const users = selectedActors.map(({ ACT_ID, ItmId }) => ACT_ID || ItmId);

      const response = await performRoleActorsAction({ roleId, users, actionType });

      setIsLoading(false);

      if (response.hasError) {
        dispatch(handleErrors(response.errors));
        return;
      }

      dispatch(forceSavedSearchRefresh('V_ACR_ACTOR_ROLE', true));
      dispatch(
        addSuccess({
          message: isDeleteAction
            ? 'Actors have been successfully unassigned'
            : 'Actors have been successfully assigned',
        })
      );
      setSelectedActors([]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`performRoleActorsAction() failed: ${error}`);
      setIsLoading(false);
    }
  };

  return (
    <div className={classes.savedSearchContainer}>
      <div className={classes.actionButtonContainer}>
        <Button
          variant="primary"
          disabled={!selectedActors.length}
          onClick={handleActionButtonClick}
        >
          {translateString(buttonLabel)}
        </Button>
      </div>
      <SavedSearchesTable
        entityName={entityName}
        initialSearchName={searchName}
        rowsPerPageOptions={[10, 25, 50]}
        enableSorting
        showSearchSelector={false}
        useSearchNameAsTitle
        customCell={NavigateLinkCell.roleActorsManagementLink}
        customFilter={customFilter}
        extraColumns={extraCurrentColumns}
      />
    </div>
  );
};

export default ActorsTable;
